import {
  Box,
  Button,
  Group,
  Modal,
  SimpleGrid,
  Title,
  Tooltip
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { StepSection } from '@/components/deployments/deployment-editor/StepSection'
import { ErrorWithReload } from '@/components/ui-shared/ErrorWithReload/ErrorWithReload'
import { InfiniteScroll } from '@/components/ui-shared/InfiniteScroll/InfiniteScroll'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useGetLibraryLogics } from '@/queries/deploymentQueries'
import {
  LibraryLogic,
  LibraryLogicType,
  NormalizedStreamBusinessLogic
} from '@/types/businessLogic'
import { CameraStreamWithDeviceId } from '@/types/camera'
import { InputSourceType } from '@/types/deployment'
import { BusinessLogicName } from './BusinessLogicName'
import { LogicHandler } from './LogicHandler/LogicHandler'
import { SelectableBusinessLogicCard } from './SelectableBusinessLogicCard/SelectableBusinessLogicCard'

const SUPPORTED_LOGICS = [
  LibraryLogicType.ObjectDetection,
  LibraryLogicType.ObjectCount
]

type LogicSelectionProps = {
  cameraStreams: CameraStreamWithDeviceId[]
  savedStreamLogics: NormalizedStreamBusinessLogic[]
  inputSource: InputSourceType
  onStreamLogicsSave: (logics: NormalizedStreamBusinessLogic[]) => void
  onGoBack: () => void
  onContinue: () => void
}

export const LogicSelection = ({
  cameraStreams,
  savedStreamLogics,
  inputSource,
  onStreamLogicsSave,
  onGoBack,
  onContinue
}: LogicSelectionProps) => {
  const isMediaFolder = inputSource === InputSourceType.MediaFolder

  const [selectedLibraryLogic, setSelectedLibraryLogic] =
    useState<LibraryLogic | null>(null)

  const [isLogicModalOpened, { open: openLogicModal, close: closeLogicModal }] =
    useDisclosure(false)

  const { data, isFetching, isError, refetch, fetchNextPage, hasNextPage } =
    useGetLibraryLogics()

  const libraryLogics = (
    data?.pages.flatMap((page) => page.results) ?? []
  ).filter((logic) => SUPPORTED_LOGICS.includes(logic.logic_type)) // do not display unsupported logics

  const handleLoadMore = () => {
    void fetchNextPage()
  }

  const handleOnReload = () => {
    void refetch()
  }

  const handleLoginModalOpen = (libraryLogic: LibraryLogic) => {
    setSelectedLibraryLogic(libraryLogic)
    openLogicModal()
  }

  const handleLogicSave = (updatedLogics: NormalizedStreamBusinessLogic[]) => {
    const savedLogics = savedStreamLogics.filter(
      (savedLogic) => savedLogic.logic_type !== selectedLibraryLogic?.logic_type
    )

    onStreamLogicsSave([...savedLogics, ...updatedLogics])

    closeLogicModal()
  }

  const hasSomeLogicSelected = savedStreamLogics.some(
    (logic) => logic.parameters.length > 0
  )

  const isLogicTypeSelected = (type: LibraryLogicType) =>
    savedStreamLogics.some(
      (logic) => logic.logic_type === type && logic.parameters.length > 0
    )

  if (isError) {
    return (
      <ErrorWithReload onReload={handleOnReload}>
        <FormattedMessage id="logic.listApiError" />
      </ErrorWithReload>
    )
  }

  return (
    <>
      <Title order={3} mb="md">
        <FormattedMessage id="deployments.step5" />
      </Title>

      <StepSection
        actions={
          <Group>
            <Button
              size="md"
              variant="outline"
              miw={200}
              leftSection={<IconChevronLeft size={16} stroke={3} />}
              onClick={onGoBack}
            >
              <FormattedMessage id="deployments.previousStep" />
            </Button>

            <Button
              size="md"
              miw={200}
              rightSection={<IconChevronRight size={16} stroke={3} />}
              onClick={onContinue}
            >
              <FormattedMessage id="deployments.nextStep" />
            </Button>
          </Group>
        }
      >
        {isMediaFolder && (
          <PlatformAlert variant="info" mb="xl">
            <FormattedMessage id="logic.cameraOnly" />
          </PlatformAlert>
        )}

        <InfiniteScroll
          hasMore={hasNextPage}
          isFetching={isFetching}
          onLoadMore={handleLoadMore}
        >
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 5 }} spacing="xl">
            {libraryLogics.map((logic) => (
              <Tooltip
                key={logic.id}
                disabled={
                  isMediaFolder ||
                  !hasSomeLogicSelected ||
                  isLogicTypeSelected(logic.logic_type)
                }
                label={<FormattedMessage id="logic.singleLogicPerStream" />}
                w={300}
                multiline
              >
                <Box>
                  <SelectableBusinessLogicCard
                    businessLogicType={logic.logic_type}
                    isSelected={isLogicTypeSelected(logic.logic_type)}
                    disabled={
                      isMediaFolder ||
                      (hasSomeLogicSelected &&
                        !isLogicTypeSelected(logic.logic_type))
                    }
                    onClick={() => handleLoginModalOpen(logic)}
                  />
                </Box>
              </Tooltip>
            ))}
          </SimpleGrid>
        </InfiniteScroll>
      </StepSection>

      <Modal
        opened={isLogicModalOpened}
        closeOnEscape={false}
        size={1480}
        title={
          selectedLibraryLogic?.logic_type && (
            <BusinessLogicName
              businessLogicType={selectedLibraryLogic.logic_type}
            />
          )
        }
        onClose={closeLogicModal}
      >
        {selectedLibraryLogic &&
          SUPPORTED_LOGICS.includes(selectedLibraryLogic.logic_type) && (
            <LogicHandler
              libraryLogic={selectedLibraryLogic}
              cameraStreams={cameraStreams}
              savedStreamLogics={savedStreamLogics}
              onCancel={closeLogicModal}
              onSave={handleLogicSave}
            />
          )}
      </Modal>
    </>
  )
}
